// frontend/src/components/PipelineMonitor/ExecucaoDetalhada.tsx

import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PipelineMetric, StatusEtapa } from '../../interfaces/pipeline.interface';
import { PipelineSteps } from './PipelineSteps';
import './ExecucaoDetalhada.css';

interface Props {
  execucao: PipelineMetric[];
  onParar: () => void;
  carregando: boolean;
}

const getStatusIcon = (status: StatusEtapa): string => {
  switch (status) {
    case 'SUCCESS': return '✅';
    case 'RUNNING': return '🔄';
    case 'STARTED': return '⏳';
    case 'FAILED':  return '❌';
    case 'SKIPPED': return '⏭️';
  }
};

const getStatusClass = (status: StatusEtapa): string => {
  switch (status) {
    case 'SUCCESS': return 'etapa-success';
    case 'RUNNING': return 'etapa-running';
    case 'STARTED': return 'etapa-pending';
    case 'FAILED':  return 'etapa-error';
    case 'SKIPPED': return 'etapa-pending';
  }
};

const getStatusColor = (status: StatusEtapa): string => {
  switch (status) {
    case 'SUCCESS': return '#66bb6a';
    case 'RUNNING': return '#4fc3f7';
    case 'STARTED': return '#ffc107';
    case 'FAILED':  return '#ef5350';
    case 'SKIPPED': return '#9e9e9e';
  }
};

const formatDuration = (ms: number | null): string => {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const segundos = Math.floor(ms / 1000);
  if (segundos < 60) return `${segundos}s`;
  const minutos = Math.floor(segundos / 60);
  return `${minutos}m ${segundos % 60}s`;
};

export const ExecucaoDetalhadaComponent: React.FC<Props> = ({
  execucao,
  onParar,
  carregando,
}) => {
  const navigate = useNavigate();

  // Estado para controlar se o modal de conclusão está aberto
  const [showModal, setShowModal] = useState(false);

  // Última linha de cada stage
  const etapas = useMemo(() => {
    const porStage = new Map<string, PipelineMetric>();
    for (const linha of execucao) {
      const atual = porStage.get(linha.stage);
      if (!atual || new Date(linha.created_at) > new Date(atual.created_at)) {
        porStage.set(linha.stage, linha);
      }
    }
    return Array.from(porStage.values());
  }, [execucao]);

  const temFailed = etapas.some(e => e.status === 'FAILED');

  // Determina se o pipeline terminou:
  // Se a etapa 'orquestracao' tem status final (SUCCESS, FAILED ou SKIPPED), o pipeline acabou.
  const orquestracao = etapas.find(e => e.stage === 'orquestracao');
  const finalizado = !!orquestracao && (
    orquestracao.status === 'SUCCESS' ||
    orquestracao.status === 'FAILED' ||
    orquestracao.status === 'SKIPPED'
  );

  // Efeito para abrir o modal automaticamente quando o pipeline terminar
  useEffect(() => {
    if (finalizado) {
      setShowModal(true);
    }
  }, [finalizado]);

  const primeiraLinha = execucao[0] ?? null;

  if (carregando) {
    return (
      <div className="execucao-detalhada-card">
        <div className="loading-state">⏳ Carregando detalhes...</div>
      </div>
    );
  }

  if (!execucao || execucao.length === 0) {
    return (
      <div className="execucao-detalhada-card">
        <div className="empty-state">📊 Nenhuma execução selecionada</div>
      </div>
    );
  }

  return (
    <div className="execucao-detalhada-card">
      <div className="detalhada-header">
        <div className="detalhada-titulo">
          <h3>📡 Monitoramento em Tempo Real</h3>
        </div>
        <div className="detalhada-acoes">
          <button className="btn-parar" onClick={onParar}>
            ⏹️ Parar
          </button>
          {finalizado && (
            <button
              onClick={() => setShowModal(true)}
              style={{
                marginLeft: '8px',
                padding: '8px 16px',
                background: '#4fc3f7',
                color: '#0a0a1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Ver Resultado
            </button>
          )}
        </div>
      </div>

      <div className="detalhada-status-atualizacao">
        {!finalizado ? (
          <>
            <span className="live-dot">●</span>
            <span>Pipeline em execução - Atualizando automaticamente</span>
          </>
        ) : (
          <>
            <span className="status-dot">●</span>
            <span>
              {temFailed ? '❌ Pipeline com falha' : '✅ Pipeline finalizado'}
            </span>
          </>
        )}
      </div>

      {etapas.filter(e => e.error_message).length > 0 && (
        <div className="detalhada-erro">
          <div className="erro-header">
            <strong>❌ ERROS NA EXECUÇÃO:</strong>
          </div>
          {etapas
            .filter(e => e.error_message)
            .map(e => (
              <div key={e.id} className="erro-mensagem">
                <strong>{e.nome_amigavel ?? e.stage}:</strong> {e.error_message}
              </div>
            ))}
        </div>
      )}

      <div className="detalhada-info">
        <div className="info-row">
          <span className="info-label">Trace ID:</span>
          <span className="info-value trace-id">
            {primeiraLinha?.trace_id ?? '—'}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Dataset:</span>
          <span className="info-value">{primeiraLinha?.dataset_name ?? '—'}</span>
        </div>
      </div>

      <PipelineSteps execucao={execucao} />

      <div className="detalhada-etapas">
        <h4>📋 Eventos registrados</h4>
        <div className="etapas-grid">
          {etapas.map(etapa => (
            <div
              key={etapa.id}
              className={`etapa-item ${getStatusClass(etapa.status)}`}
            >
              <div className="etapa-status">{getStatusIcon(etapa.status)}</div>
              <div className="etapa-info">
                <span className="etapa-nome">
                  {etapa.nome_amigavel ?? etapa.stage}
                </span>
                <span
                  className="etapa-status-texto"
                  style={{ color: getStatusColor(etapa.status) }}
                >
                  {etapa.status}
                </span>
                {etapa.duration_ms !== null && (
                  <span className="etapa-duracao">
                    {formatDuration(etapa.duration_ms)}
                  </span>
                )}
                {etapa.error_message && (
                  <span className="etapa-erro">❌ {etapa.error_message}</span>
                )}
              </div>
              <div className="etapa-tempo">
                <span className="etapa-inicio">
                  {etapa.started_at ?? '—'}
                </span>
                {etapa.finished_at && (
                  <span className="etapa-fim">
                    → {etapa.finished_at}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL DE CONCLUSÃO (OVERLAY FIXO) */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
            border: `2px solid ${temFailed ? '#ef5350' : '#66bb6a'}`,
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {temFailed ? '❌' : '🎉'}
            </div>
            <h2 style={{
              color: temFailed ? '#ef5350' : '#66bb6a',
              margin: '0 0 12px 0',
              fontSize: '24px',
            }}>
              {temFailed ? 'Processamento Falhou' : 'Processamento Concluído!'}
            </h2>
            <p style={{
              color: '#e0e0e0',
              fontSize: '15px',
              lineHeight: 1.6,
              margin: '0 0 24px 0',
            }}>
              {temFailed
                ? 'Houve um erro durante o processamento. Verifique os detalhes na tela para entender o que aconteceu.'
                : 'Seu modelo está pronto! Você pode agora explorar os resultados ou comparar com outros treinos.'}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {!temFailed && (
                <>
                  <button
                    onClick={() => { setShowModal(false); navigate('/scientific-dashboard'); }}
                    style={{
                      background: '#4fc3f7',
                      color: '#0a0a1a',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '15px',
                    }}
                  >
                    📖 Ver a Jornada do Dado
                  </button>
                  <button
                    onClick={() => { setShowModal(false); navigate('/ml-compare'); }}
                    style={{
                      background: 'transparent',
                      color: '#4fc3f7',
                      border: '1px solid #4fc3f7',
                      borderRadius: '8px',
                      padding: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '15px',
                    }}
                  >
                    🤖 Comparar Modelos
                  </button>
                </>
              )}
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  color: '#8899aa',
                  border: '1px solid #1a4a7a',
                  borderRadius: '8px',
                  padding: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '15px',
                }}
              >
                ✖️ Fechar e ver os detalhes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecucaoDetalhadaComponent;